import { ReactNode, useEffect, useState } from 'react';
import { UnicodeEntity } from '@mews/enums';
import { OptimusTextKey } from '@optimus-web/localization';
import { InputValue } from '@optimus-web/common';
import { VisuallyHidden } from '../helpers';

/*
 * Shared utilities for building accessible form field markup and behavior.
 *
 * This file contains reusable functions for:
 * - Building accessible labels with proper formatting and separators
 * - Debouncing character count updates to prevent screen reader interruptions
 * - Creating character count content with consistent localization
 * - Generating complete accessibility markup structures with proper ARIA attributes
 *
 */

export const buildAccessibleLabel = (parts: (ReactNode | undefined | null)[]) => {
    const validParts = parts.filter(Boolean);

    if (!validParts.length) return null;

    if (validParts.length === 1) return <span>{validParts[0]}</span>;

    return validParts.map((part, index) => (
        <span key={`part-${typeof part === 'string' ? part : index}`}>
            {part}
            {index < validParts.length - 1 && (
                <>
                    {UnicodeEntity.Comma}
                    {UnicodeEntity.Space}
                </>
            )}
        </span>
    ));
};

export const useCharacterCountDebouncing = (value: InputValue | undefined = '') => {
    const [debouncedCharacterCount, setDebouncedCharacterCount] = useState(0);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedCharacterCount(value?.toString().length || 0);
            /* 1.5 seconds delay to prevent screen reader interruptions.
             * This is a workaround to avoid the screen reader reading the value changes too quickly.
             * This is a known issue with screen readers.
             */
        }, 1500);

        return () => window.clearTimeout(timer);
    }, [value]);

    return debouncedCharacterCount;
};

export const buildCharacterCountContent = (
    maxCharacterCount: number | undefined,
    debouncedCharacterCount: number,
    t: (key: OptimusTextKey, params?: { count: number }) => string
) =>
    maxCharacterCount ? t(OptimusTextKey.CharactersLeft, { count: maxCharacterCount - debouncedCharacterCount }) : null;

export const buildAccessibilityMarkup = (
    accessibleLabelId: string | undefined,
    accessibleLabelContent: React.ReactNode,
    accessibleDescriptionId: string | undefined,
    accessibleDescriptionContent: React.ReactNode,
    accessibleCharacterCountContent: React.ReactNode
) => (
    <>
        {accessibleLabelContent ? (
            <VisuallyHidden aria-hidden id={accessibleLabelId}>
                {accessibleLabelContent}
            </VisuallyHidden>
        ) : null}
        {accessibleDescriptionContent ? (
            <VisuallyHidden aria-hidden id={accessibleDescriptionId}>
                {accessibleDescriptionContent}
            </VisuallyHidden>
        ) : null}
        {accessibleCharacterCountContent ? (
            <VisuallyHidden aria-live="polite" aria-atomic="false">
                {accessibleCharacterCountContent}
            </VisuallyHidden>
        ) : null}
    </>
);
